import { RTKQueryTag } from 'src/common/constants';
import type { ApiResponse, PaginatedResponse } from 'src/common/interfaces';
import { apiSlice } from 'src/features/api/api-slice';
import type { GeneralLeaderboardUser } from 'src/features/leaderboards/leaderboards.types';
import type { ConnectionDataProps } from './user-connections.types';

interface PaginationParams {
  userId: string;
  page?: number;
  limit?: number;
}

export type SuggestedFollowUser = GeneralLeaderboardUser & {
  suggestionSource?: 'follow_back' | 'friends_of_friends' | 'same_path' | 'leaderboard';
  suggestionMeta?: { sampleFollowerUsername?: string; mutualCount?: number; pathName?: string };
};

export const extendedApiSlice = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getUserFollowers: builder.query<ApiResponse<PaginatedResponse<ConnectionDataProps>>, PaginationParams>({
      query: ({ userId, page = 1, limit = 10 }) => ({
        method: 'GET',
        url: `/follows/followers`,
        params: { userId, page, limit },
      }),
      providesTags: [RTKQueryTag.UserFollowers],
    }),
    getUserFollowees: builder.query<ApiResponse<PaginatedResponse<ConnectionDataProps>>, PaginationParams>({
      query: ({ userId, page = 1, limit = 10 }) => ({
        method: 'GET',
        url: `/follows/followees`,
        params: { userId, page, limit },
      }),
      providesTags: [RTKQueryTag.UserFollowees],
    }),
    getUserSuggestedFollowees: builder.query<
      ApiResponse<PaginatedResponse<SuggestedFollowUser>>,
      { country: string; page: number; limit: number; maxPages?: number }
    >({
      query: ({ country, page, limit, maxPages = 3 }) => ({
        method: 'GET',
        url: `/follows/followees/suggested`,
        params: { country, page, limit, maxPages },
      }),
      providesTags: [RTKQueryTag.UserSuggestedFollowees],
    }),
    followUser: builder.mutation<ApiResponse<void>, string>({
      query: (followeeId) => ({
        method: 'POST',
        url: `/follows`,
        body: { followeeId },
      }),
      invalidatesTags: [RTKQueryTag.UserFollowers, RTKQueryTag.UserFollowees],
    }),
    followUserByUsername: builder.mutation<ApiResponse<void>, string>({
      query: (followeeUsername) => ({
        method: 'POST',
        url: `/follows/username`,
        body: { username: followeeUsername },
      }),
      invalidatesTags: [RTKQueryTag.UserFollowers, RTKQueryTag.UserFollowees],
    }),
    isFollowing: builder.query<ApiResponse<boolean>, string>({
      query: (followeeId) => ({
        method: 'GET',
        url: `/follows/is-following`,
        params: { followeeId },
      }),
      providesTags: [RTKQueryTag.UserFollowers],
    }),
    unfollowUser: builder.mutation<ApiResponse<void>, string>({
      query: (followeeId) => ({
        method: 'DELETE',
        url: `/follows`,
        params: { followeeId },
      }),
      invalidatesTags: [RTKQueryTag.UserFollowers, RTKQueryTag.UserFollowees],
    }),
    getUserFollowCount: builder.query<ApiResponse<{ followersCount: number; followeesCount: number }>, string>({
      query: (userId) => ({
        method: 'GET',
        url: `/follows/count`,
        params: { userId },
      }),
      providesTags: [RTKQueryTag.UserFollowers, RTKQueryTag.UserFollowees],
    }),
    inviteToFollow: builder.mutation<
      ApiResponse<
        { result: 'invited' | 'alreadyInvited' } | { result: 'followed' | 'alreadyFollowing'; username: string }
      >,
      { email: string }
    >({
      query: (body) => ({
        url: '/follows/invite',
        method: 'POST',
        body,
      }),
      invalidatesTags: [RTKQueryTag.UserFollowees],
    }),
  }),
});

export const {
  useGetUserFollowersQuery,
  useGetUserFolloweesQuery,
  useFollowUserMutation,
  useFollowUserByUsernameMutation,
  useIsFollowingQuery,
  useUnfollowUserMutation,
  useGetUserFollowCountQuery,
  useGetUserSuggestedFolloweesQuery,
  useInviteToFollowMutation,
} = extendedApiSlice;
